<template>
  <div id="app" class="article-page">
    <HeadNav selected-language="en" page="blog" :gotodownload="gotodownload" />
    <main class="article-main">
      <article class="article-content">
        <header class="article-hero">
          <p class="article-kicker">Customization Guide</p>
          <h1>How to Change Themes in GB WhatsApp</h1>
          <p class="article-lead">
            The default GB WhatsApp interface may not be the look you want. You might prefer a dark layout,
            different chat bubble colors, a new wallpaper, or a theme that changes several parts of the app at once.
          </p>
        </header>

        <img
          class="article-image"
          src="@/assets/blog-cover-themes.webp"
          alt="Change themes in GB WhatsApp">

        <p>
          <a href="/">GB WhatsApp</a> builds may offer theme and appearance settings, but the available options
          depend on the version you're using. Before downloading an external theme, check what you can customize
          directly inside the app.
        </p>

        <h2>How to Change the Theme in GB WhatsApp</h2>
        <p>Start with the built-in theme settings. This is usually the simplest way to change the overall appearance without importing a separate file.</p>
        <ol class="article-list article-list--ordered">
          <li>Open GB WhatsApp on your Android phone.</li>
          <li>Tap the three-dot menu in the upper-right corner.</li>
          <li>Open <strong>GB Settings</strong>.</li>
          <li>Look for <strong>Themes</strong>, <strong>Theme Store</strong>, or a similarly named option.</li>
          <li>Browse the available themes, if a theme gallery is provided.</li>
          <li>Select a theme and tap the available apply option.</li>
          <li>Return to your chats to check the new appearance.</li>
        </ol>
        <p>
          The exact menu names may differ between builds. If you don't see a theme gallery, check the appearance or
          customization sections before assuming your version supports every theme feature described online.
        </p>

        <h2>What Can You Change with a GB WhatsApp Theme?</h2>
        <p>Not every theme changes the same elements. Some affect much of the interface, while others focus on particular colors or backgrounds.</p>

        <h3>Dark and Minimal Themes</h3>
        <p>
          A dark theme can make the interface less bright in dim surroundings. Minimal themes may use fewer decorative
          elements and simpler colors. If you prefer a clean layout, look for themes with readable text, clear message
          bubbles, and good contrast rather than choosing one based on its preview alone.
        </p>

        <h3>Chat Bubble Colors</h3>
        <p>
          You may want incoming messages to appear in one color and your own messages in another. Some GB WhatsApp builds
          provide separate customization options for chat bubble colors. If you only want to change the bubbles, check
          the chat or color settings first. Applying an entirely new theme may change more of the interface than you intended.
        </p>

        <h3>Chat Wallpapers</h3>
        <p>
          A new wallpaper can make a conversation feel different without requiring a complete interface redesign. Check
          whether your version offers a global wallpaper setting, individual chat backgrounds, or both. These options may
          be separate from the main theme manager.
        </p>

        <h3>Fonts and Other Interface Colors</h3>
        <p>
          Some builds also advertise font styles, toolbar colors, and additional visual settings. Their availability varies,
          so don't assume every option is included in your installed version.
        </p>

        <h2>How to Import a Custom GB WhatsApp Theme</h2>
        <p>If you have downloaded a theme file, first check whether your GB WhatsApp build supports importing external themes.</p>
        <ol class="article-list article-list--ordered">
          <li>Open GB Settings and enter the Themes section.</li>
          <li>Look for an option such as <strong>Import Theme</strong>, <strong>Load Theme</strong>, or a similar command.</li>
          <li>If available, select the theme file from your phone's storage.</li>
          <li>Follow the on-screen instructions to apply it.</li>
          <li>Return to the main interface and check the result.</li>
        </ol>
        <p>
          The supported file format and import process can vary by build. Don't rename a file or move it into a guessed folder
          just because a tutorial for another version recommends doing so. Be cautious with third-party downloads. A theme
          advertised online may be outdated, incompatible, or bundled with unwanted software. Never install an unknown APK
          just to obtain a new color scheme.
        </p>

        <h2>How to Restore the Default GB WhatsApp Theme</h2>
        <p>A custom theme may look different from its preview, make text difficult to read, or simply stop matching your preferences.</p>
        <ol class="article-list article-list--ordered">
          <li>Open GB WhatsApp and go to GB Settings.</li>
          <li>Open the Themes or appearance section.</li>
          <li>Look for <strong>Default</strong>, <strong>Reset</strong>, or <strong>Restore Default</strong>, if available.</li>
          <li>Apply the option and check your chats again.</li>
        </ol>
        <p>
          If your build provides separate settings for fonts, wallpapers, or colors, resetting the main theme may not restore
          every individual customization. Check those settings separately if parts of the interface still look different.
        </p>

        <h2>Why Is My GB WhatsApp Theme Not Working?</h2>
        <p>If a theme doesn't apply correctly, check these common causes before reinstalling the app.</p>
        <ul class="article-list">
          <li><strong>The theme is incompatible with your version.</strong> A theme created for another build may not display correctly. Try a theme supported by your installed version.</li>
          <li><strong>The import option is missing.</strong> Your build may not support importing external theme files. Check the available settings rather than following instructions for a different release.</li>
          <li><strong>The interface hasn't refreshed.</strong> Close and reopen the app to see whether the change takes effect.</li>
          <li><strong>Text or icons are difficult to see.</strong> The selected colors may have poor contrast. Switch to a simpler theme or restore the default settings.</li>
        </ul>
        <p>Before making major changes or reinstalling GB WhatsApp, back up important chats. Avoid clearing app data as a first troubleshooting step.</p>

        <h2>Will Changing the Theme Affect Your Chats?</h2>
        <p>
          Changing the visual theme is intended to change the interface, not your phone number or account. However, GB WhatsApp
          is an unofficial modified application, and its behavior can differ between builds. Back up important conversations
          before updating the app or making broader changes. A theme also does not improve the app's security or make a
          modified version official. Be selective about the files you download and the permissions you grant.
        </p>

        <h2>Make GB WhatsApp Look the Way You Want</h2>
        <p>
          If you want a different overall appearance, start with the built-in Themes section. If you only want new chat colors
          or a wallpaper, check whether those elements can be customized separately. For external themes, confirm that your version
          supports the file format before downloading anything. If a theme fails to apply, restore the default appearance rather
          than repeatedly installing files from unknown sources.
        </p>

        <p>
          Related guides:
          <a href="/change-call-privacy-settings-gb-whatsapp/">Call privacy settings</a>
          ·
          <a href="/blog/">More GB WhatsApp guides</a>
        </p>

        <div class="article-download">
          <div>
            <strong>Need the latest APK?</strong>
            <span>Download the GB WhatsApp build shown on this site for Android.</span>
          </div>
          <a :href="site.downloadPath" class="primary-download">Download APK</a>
        </div>
      </article>
    </main>
  </div>
</template>

<script>
import HeadNav from '@/components/HeadNav.vue'
import site from '@/config/site.json'

export default {
  name: 'BlogChangeThemes',
  components: { HeadNav },
  data() {
    return { site }
  },
  methods: {
    gotodownload() {
      window.location.href = site.downloadPath
    },
  },
}
</script>

<style lang="scss" scoped>
.article-page {
  min-height: 100vh;
  background: #f7f8fb;
}

.article-main {
  padding: 46px 16px 72px;
}

.article-content {
  width: min(880px, 100%);
  margin: 0 auto;
  padding: 42px 48px 50px;
  background: #fff;
  border: 1px solid #e3e7ef;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(31, 45, 61, 0.08);
  color: #27313f;

  h1 {
    margin: 0 0 18px;
    color: #16243a;
    font-size: clamp(30px, 5vw, 46px);
    line-height: 1.15;
  }

  h2 {
    margin: 38px 0 12px;
    color: #182b49;
    font-size: clamp(22px, 3vw, 28px);
    line-height: 1.3;
  }

  h3 {
    margin: 28px 0 10px;
    color: #1e3558;
    font-size: 20px;
    line-height: 1.35;
  }

  p {
    margin: 0 0 18px;
    font-size: 17px;
    line-height: 1.75;
  }

  a {
    color: #245daf;
  }
}

.article-kicker {
  margin: 0 0 10px !important;
  color: #e35b24;
  font-size: 13px !important;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.article-lead {
  color: #536173;
  font-size: 19px !important;
}

.article-image {
  display: block;
  width: 100%;
  height: auto;
  margin: 26px auto 30px;
  border: 1px solid #e0e6ef;
  border-radius: 12px;
  background: #f4f7fb;
}

.article-list {
  margin: 0 0 20px 22px;
  padding: 0;

  li {
    margin-bottom: 9px;
    line-height: 1.65;
  }
}

.article-list--ordered {
  list-style: decimal;
}

.article-download {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 42px;
  padding: 22px 24px;
  background: #eef4ff;
  border: 1px solid #ccdcf7;
  border-radius: 12px;

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 4px;
    color: #52637a;
    font-size: 14px;
  }
}

.primary-download {
  flex: 0 0 auto;
  padding: 11px 20px;
  border-radius: 8px;
  background: #1f65c6;
  color: #fff !important;
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 640px) {
  .article-main {
    padding: 22px 12px 48px;
  }

  .article-content {
    padding: 28px 20px 34px;
    border-radius: 12px;
  }

  .article-download {
    align-items: stretch;
    flex-direction: column;
  }

  .primary-download {
    text-align: center;
  }
}
</style>
